import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Loader2, MapPin, Plus, Users, ExternalLink, Video, Sparkles, CheckCircle2 } from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { popListEvents, popCreateEvent, popRegisterEvent, popMyEvents, popEnroll, popMyEnrollments } from '../../lib/popApi';

const KIND_META = {
  webinar:       { label: 'Webinar',        color: 'text-cyan-300',    bg: 'bg-cyan-500/10' },
  workshop:      { label: 'Workshop',       color: 'text-emerald-300', bg: 'bg-emerald-500/10' },
  mentorship:    { label: 'Mentorship',     color: 'text-amber-300',   bg: 'bg-amber-500/10' },
  office_hours:  { label: 'Office Hours',   color: 'text-violet-300',  bg: 'bg-violet-500/10' },
  panel:         { label: 'Panel',          color: 'text-rose-300',    bg: 'bg-rose-500/10' },
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const EventCard = ({ ev, onRegister, mine, enrolled, onEnroll, user }) => {
  const meta = KIND_META[ev.kind] || KIND_META.webinar;
  const [busy, setBusy] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [localEnrolled, setLocalEnrolled] = useState(enrolled);
  const [msg, setMsg] = useState('');
  const doReg = async () => {
    setBusy(true); setMsg('');
    try { await onRegister(ev.id); setMsg('Registered ✓'); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Failed'); }
    finally { setBusy(false); }
  };
  const doEnroll = async () => {
    setEnrolling(true);
    try { await onEnroll(ev.id); setLocalEnrolled(true); }
    catch (_e) { /* noop */ }
    setEnrolling(false);
  };
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5" data-testid={`event-card-${ev.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${meta.bg} ${meta.color} font-bold`}>{meta.label}</span>
        <span className="text-[10px] text-white/50 tabular-nums">{ev.registered_count || 0} registered{ev.capacity ? ` / ${ev.capacity}` : ''}</span>
      </div>
      <div className="text-base font-serif font-semibold text-white mb-1">{ev.title}</div>
      <div className="text-[11px] text-white/50 mb-2">hosted by <span className="text-white/80 font-medium">{ev.host_name}</span></div>
      <p className="text-xs text-white/70 leading-relaxed line-clamp-3 mb-3">{ev.description}</p>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-white/50 mb-3">
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmt(ev.starts_at)}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ev.duration_minutes} min</span>
        {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.location}</span>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {mine && ev.join_url ? (
          <a href={ev.join_url} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold"
             data-testid={`event-join-${ev.id}`}>
            <Video className="w-3.5 h-3.5" /> Join <ExternalLink className="w-3 h-3" />
          </a>
        ) : !mine ? (
          <button onClick={doReg} disabled={busy}
            data-testid={`event-register-${ev.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-xs font-semibold">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Register
          </button>
        ) : (
          <span className="text-xs text-white/40 italic">You are hosting this</span>
        )}
        {user && !mine && (
          <button onClick={doEnroll} disabled={enrolling || localEnrolled}
            data-testid={`event-enroll-${ev.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
              localEnrolled
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default'
                : 'bg-white/5 border border-white/10 text-white/80 hover:bg-white/10'
            }`}>
            {enrolling ? <Loader2 className="w-3 h-3 animate-spin" /> :
              localEnrolled ? <><CheckCircle2 className="w-3 h-3" /> Added to Learning</> :
              <><Sparkles className="w-3 h-3" /> Add to Learning</>}
          </button>
        )}
        {msg && <span className="text-[11px] text-emerald-300">{msg}</span>}
      </div>
    </div>
  );
};

const CreateEventForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', kind: 'webinar', starts_at: '',
    duration_minutes: 60, location: 'Online', join_url: '', capacity: 0,
  });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payload = { ...form, duration_minutes: parseInt(form.duration_minutes, 10) || 60, capacity: parseInt(form.capacity, 10) || 0 };
      await popCreateEvent(payload);
      setOpen(false); setForm({ ...form, title: '', description: '', starts_at: '' });
      onCreated();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not create'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="event-create-open"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold">
          <Plus className="w-4 h-4" /> Host an event or mentorship
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 grid sm:grid-cols-2 gap-3">
          <input placeholder="Event title" value={form.title} onChange={upd('title')} data-testid="event-form-title"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <textarea placeholder="Description" value={form.description} onChange={upd('description')} data-testid="event-form-desc" rows={3}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <select value={form.kind} onChange={upd('kind')} data-testid="event-form-kind"
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
            {Object.entries(KIND_META).map(([id, m]) => (<option key={id} value={id} className="bg-slate-900">{m.label}</option>))}
          </select>
          <input type="datetime-local" value={form.starts_at} onChange={upd('starts_at')} data-testid="event-form-when"
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white" />
          <input type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={upd('duration_minutes')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Location" value={form.location} onChange={upd('location')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Join URL (Zoom / Meet)" value={form.join_url} onChange={upd('join_url')}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input type="number" placeholder="Capacity (0 = unlimited)" value={form.capacity} onChange={upd('capacity')}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          {err && <div className="text-xs text-rose-300 col-span-2">{err}</div>}
          <div className="col-span-2 flex gap-2">
            <button onClick={submit} disabled={busy} data-testid="event-form-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-sm font-semibold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Publish
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm text-white/70">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const PoPEvents = () => {
  const { user } = usePoPAuth();
  const [kind, setKind] = useState('');
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState({ hosted: [], registered: [] });
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState('all');

  const load = async () => {
    setBusy(true);
    try {
      const params = { upcoming_only: true };
      if (kind) params.kind = kind;
      const r = await popListEvents(params);
      setItems(r.results || []);
      if (user) {
        const my = await popMyEvents();
        setMine({ hosted: my.hosted || [], registered: my.registered || [] });
        try {
          const me = await popMyEnrollments();
          setEnrolledIds(new Set((me.results || []).filter((e) => e.subject_type === 'event').map((e) => e.subject_id)));
        } catch (_e) { /* noop */ }
      }
    } catch (_e) { setItems([]); } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, user]);

  const doReg = async (id) => { await popRegisterEvent(id); await load(); };
  const doEnroll = async (id) => {
    await popEnroll('event', id);
    setEnrolledIds((prev) => new Set([...prev, id]));
  };
  const canHost = user && user.role !== 'unassigned';

  const visible = tab === 'all' ? items : tab === 'hosted' ? mine.hosted : mine.registered;

  return (
    <PoPShell activeTab="events">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">EVENTS & MENTORSHIP</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Sessions across the confluence.</h1>
        <p className="text-sm text-white/60 max-w-2xl mt-1">Webinars, workshops, mentorship, office hours and panels — one calendar for the ecosystem.</p>
      </div>

      {canHost && <CreateEventForm onCreated={load} />}

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'all',        label: 'Upcoming' },
          { id: 'hosted',     label: `Hosting (${mine.hosted.length})` },
          { id: 'registered', label: `Registered (${mine.registered.length})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`event-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
              tab === t.id ? 'bg-amber-300 text-slate-900 border-amber-300' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
            }`}>{t.label}</button>
        ))}
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="event-kind-filter"
          className="ml-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-white">
          <option value="">All kinds</option>
          {Object.entries(KIND_META).map(([id, m]) => (<option key={id} value={id} className="bg-slate-900">{m.label}</option>))}
        </select>
      </div>

      {busy ? (
        <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
          <Calendar className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/60">No events {tab === 'all' ? 'scheduled' : 'in this list'} yet.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((ev) => (
            <EventCard key={ev.id} ev={ev} onRegister={doReg}
              enrolled={enrolledIds.has(ev.id)} onEnroll={doEnroll} user={user}
              mine={tab === 'hosted' || (tab === 'registered' ? true : mine.hosted.some((h) => h.id === ev.id) || mine.registered.some((r) => r.id === ev.id))}
            />
          ))}
        </div>
      )}
    </PoPShell>
  );
};

export default PoPEvents;
